import { Children, Fragment, cloneElement, isValidElement, useEffect, useState } from 'react';
import type { HTMLAttributes, KeyboardEvent, MouseEvent, ReactElement, ReactNode } from 'react';
import clsx from 'clsx';
import AlertModal from '../AlertModal/AlertModal';
import { mobileTableClass, mobileTableViewportClass } from '../../utils/mobileResponsiveTable';

const getNodeText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (node == null || typeof node === 'boolean') {
    return '';
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join(' ');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return Children.toArray(node.props.children).map(getNodeText).join(' ');
  }

  return '';
};

const normalizeCellText = (value: string): string => value.replace(/\s+/g, ' ').trim();

const getElementName = (node: ReactElement): string => {
  if (typeof node.type === 'string') {
    return node.type;
  }

  if (typeof node.type === 'function') {
    return node.type.name ?? '';
  }

  return '';
};

const isPreviewableNode = (node: ReactNode): boolean => {
  if (typeof node === 'string' || typeof node === 'number') {
    return true;
  }

  if (node == null || typeof node === 'boolean') {
    return false;
  }

  if (Array.isArray(node)) {
    return node.every(isPreviewableNode);
  }

  if (!isValidElement<{ children?: ReactNode }>(node)) {
    return false;
  }

  if (node.type === Fragment) {
    return Children.toArray(node.props.children).every(isPreviewableNode);
  }

  const elementName = getElementName(node);
  if (elementName.includes('Tooltip')) {
    return Children.toArray(node.props.children).every(isPreviewableNode);
  }

  if (typeof node.type !== 'string') {
    return false;
  }

  if (['a', 'button', 'input', 'select', 'textarea', 'label'].includes(node.type)) {
    return false;
  }

  return Children.toArray(node.props.children).every(isPreviewableNode);
};

const isTableCellElement = (
  node: ReactNode
): node is ReactElement<HTMLAttributes<HTMLTableCellElement>> =>
  isValidElement(node) && (node.type === 'th' || node.type === 'td');

const isTableRowElement = (
  node: ReactNode
): node is ReactElement<HTMLAttributes<HTMLTableRowElement>> =>
  isValidElement(node) && node.type === 'tr';

const isTableSectionElement = (
  node: ReactNode
): node is ReactElement<HTMLAttributes<HTMLTableSectionElement>> =>
  isValidElement(node) && (node.type === 'thead' || node.type === 'tbody');

const enhanceTableCell = (
  cell: ReactElement<HTMLAttributes<HTMLTableCellElement>>,
  isMobile: boolean,
  onPreview: (value: string) => void
) => {
  const cellText = normalizeCellText(getNodeText(cell.props.children));
  const isPreviewCell =
    isMobile && cell.type === 'td' && cellText.length > 0 && isPreviewableNode(cell.props.children);

  return cloneElement(cell, {
    title:
      cell.props.title ?? (isPreviewCell || cell.type === 'th' ? cellText || undefined : undefined),
    className: clsx(
      cell.props.className,
      isPreviewCell &&
        'max-sm:cursor-pointer max-sm:overflow-hidden max-sm:text-ellipsis max-sm:whitespace-nowrap'
    ),
    onClick: isPreviewCell
      ? (event: MouseEvent<HTMLTableCellElement>) => {
          cell.props.onClick?.(event);
          if (!event.defaultPrevented) {
            onPreview(cellText);
          }
        }
      : cell.props.onClick,
    onKeyDown: isPreviewCell
      ? (event: KeyboardEvent<HTMLTableCellElement>) => {
          cell.props.onKeyDown?.(event);
          if (event.defaultPrevented) {
            return;
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onPreview(cellText);
          }
        }
      : cell.props.onKeyDown,
    role: isPreviewCell ? 'button' : cell.props.role,
    tabIndex: isPreviewCell ? 0 : cell.props.tabIndex,
  });
};

const enhanceTableRow = (
  row: ReactElement<HTMLAttributes<HTMLTableRowElement>>,
  isMobile: boolean,
  onPreview: (value: string) => void
) =>
  cloneElement(row, {
    children: Children.map(row.props.children, child =>
      isTableCellElement(child) ? enhanceTableCell(child, isMobile, onPreview) : child
    ),
  });

const enhanceTableSection = (
  section: ReactElement<HTMLAttributes<HTMLTableSectionElement>>,
  isMobile: boolean,
  onPreview: (value: string) => void
) =>
  cloneElement(section, {
    children: Children.map(section.props.children, child =>
      isTableRowElement(child) ? enhanceTableRow(child, isMobile, onPreview) : child
    ),
  });

interface MobileResponsiveTableProps {
  containerClassName: string;
  tableClassName: string;
  children: ReactNode;
  stickyHeaderTestId?: string;
}

export default function MobileResponsiveTable({
  containerClassName,
  tableClassName,
  children,
  stickyHeaderTestId,
}: MobileResponsiveTableProps) {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const childNodes = Children.toArray(children);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const handleMediaChange = () => setIsMobile(mediaQuery.matches);

    handleMediaChange();
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const openPreview = (value: string) => {
    setPreviewContent(value);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewContent('');
  };

  const headerNode = childNodes.find(
    (child): child is ReactElement<HTMLAttributes<HTMLTableSectionElement>> =>
      isValidElement(child) && child.type === 'thead'
  );
  const enhancedHeader = headerNode ? enhanceTableSection(headerNode, isMobile, openPreview) : null;
  const hiddenHeader = enhancedHeader
    ? cloneElement(enhancedHeader, {
        className: clsx(enhancedHeader.props.className, 'max-sm:sr-only'),
      })
    : null;
  const bodyNodes = childNodes
    .filter(child => !(isValidElement(child) && child.type === 'thead'))
    .map(child => {
      if (isTableSectionElement(child)) {
        return enhanceTableSection(child, isMobile, openPreview);
      }

      if (isTableRowElement(child)) {
        return enhanceTableRow(child, isMobile, openPreview);
      }

      return child;
    });

  return (
    <>
      <div
        className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm sm:hidden"
        data-testid={stickyHeaderTestId}
      >
        <div className="overflow-hidden border-b border-[#e4e4e7]">
          <table
            className={clsx(tableClassName, mobileTableClass, 'will-change-transform')}
            aria-hidden="true"
            style={{ transform: `translateX(${-scrollLeft}px)` }}
          >
            {enhancedHeader}
          </table>
        </div>
      </div>

      <div
        className={clsx(containerClassName, mobileTableViewportClass)}
        onScroll={event => setScrollLeft(event.currentTarget.scrollLeft)}
      >
        <table className={clsx(tableClassName, mobileTableClass)}>
          {hiddenHeader}
          {bodyNodes}
        </table>
      </div>

      <AlertModal
        isOpen={previewOpen}
        title="Full Content"
        content={previewContent}
        confirmText="Close"
        type="info"
        onConfirm={closePreview}
        autoClose={false}
      />
    </>
  );
}
